import type { RefObject } from "react";

import {
  FallbackVideoResourcesCdnUrl,
  WelcomeFeatures,
  WelcomeText,
} from "@/constants/Onboarding";
import { OnboardingWelcomeTestID, TextTestID } from "@/constants/TestID";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import { captureException } from "@/services/AppMonitor";
import VideoPlayerCard from "@/ui/components/onboarding/welcome/videoPlayerCard/VideoPlayerCard";
import type { VideoPlayerHandle } from "@/ui/components/onboarding/welcome/videoPlayerCard/VideoPlayerCard";
import type { WelcomeTileProps } from "@/ui/components/onboarding/welcome/WelcomeTile";
import WelcomeTile from "@/ui/components/onboarding/welcome/WelcomeTile";
import { Text } from "@/ui/core";
import { VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface WelcomePageProps {
  videoRef?: RefObject<null | VideoPlayerHandle>;
}

const handleVideoError = (error: string) => {
  captureException(
    new Error(
      `WelcomePage VideoPlayerCard received unhandled error '${error}'`,
    ),
  );
};

const WelcomePage = ({ videoRef }: WelcomePageProps) => {
  const baseTestID = OnboardingWelcomeTestID.Primary;
  const { appVideoResourcesPrefixUrl } = useFeatureFlags();

  // Use feature flag CDN URL or fallback to prod CDN if flag is not yet loaded
  // LaunchDarkly flags may return empty string during initial hydration on web
  const prefixUrl = appVideoResourcesPrefixUrl || FallbackVideoResourcesCdnUrl;
  const videoUrl = `${prefixUrl}${WelcomeText.videoUrl}`;

  return (
    <VStack gap={24} paddingTop={22}>
      <VideoPlayerCard
        onError={handleVideoError}
        testID={baseTestID}
        thumbnail={WelcomeText.thumbnail}
        title={WelcomeText.videoTitle}
        videoRef={videoRef}
        videoUrl={videoUrl}
      />
      <VStack
        alignItems="center"
        gap={12}
        paddingHorizontal={12}
        paddingTop={24}
      >
        <Text
          color={ColorToken.Primary90Variant}
          testID={joinTestIDs(baseTestID, TextTestID.Title)}
          variant="h2"
        >
          Welcome to Equip!
        </Text>
        <Text
          color={ColorToken.Primary90Variant}
          testID={joinTestIDs(baseTestID, TextTestID.Description)}
          textAlign="center"
        >
          The Equip app is the home for everything you need to stay on top of
          your treatment.
        </Text>
      </VStack>
      <VStack gap={12}>
        {WelcomeFeatures.map((feature: WelcomeTileProps) => {
          return (
            <WelcomeTile
              icon={feature.icon}
              key={feature.title}
              title={feature.title}
            />
          );
        })}
      </VStack>
    </VStack>
  );
};

export default WelcomePage;
